/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, memo, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, type GeoJSONProps, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { formatDecimal } from '@/lib/utils';
import { PlotFeature, PlotFeatureCollection, ZoneFeature, ZoneFeatureCollection } from '@/lib/types';
import { runAutoZoom } from '@/lib/mapZoom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type MapViewProps = {
  zones: ZoneFeature[];
  plots: PlotFeature[];
  initialCenter?: [number, number];
  initialZoom?: number;
};

function AutoZoom({
  zones,
  plots,
  fallbackBounds,
}: {
  zones: ZoneFeature[];
  plots: PlotFeature[];
  fallbackBounds: L.LatLngBoundsExpression;
}) {
  const map = useMap();
  useEffect(() => {
    runAutoZoom(map, zones, plots, fallbackBounds);
  }, [map, zones, plots, fallbackBounds]);
  return null;
}

const MapView: FC<MapViewProps> = ({ zones, plots, initialCenter = [23.6, 58.5], initialZoom = 7 }) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n?.language || '').toLowerCase().startsWith('ar');

  const zonesFC: ZoneFeatureCollection = useMemo(() => ({ type: 'FeatureCollection', features: zones }), [zones]);
  const plotsFC: PlotFeatureCollection = useMemo(() => ({ type: 'FeatureCollection', features: plots }), [plots]);

  const OMAN_BOUNDS = L.latLngBounds([16.5, 52.0], [26.6, 60.0]);

  const zoneStyle: L.PathOptions = {
    color: '#005287',
    fillColor: '#cce6ff',
    fillOpacity: 0.4,
    weight: 1.5,
  };
  const plotStyle: L.PathOptions = {
    color: '#15803d',
    fillColor: '#bbf7d0',
    fillOpacity: 0.5,
    weight: 1.2,
  };

  const onEachZone: GeoJSONProps['onEachFeature'] = (feature, layer) => {
    const p = (feature as ZoneFeature).properties;
    const zoneName = isAr ? (p.ZoneNameAr ?? p.ZoneName ?? p.ZoneNameEn) : (p.ZoneNameEn ?? p.ZoneName ?? p.ZoneNameAr);
    const zoneDesc = isAr
      ? (p.ZoneDesripAr ?? p.ZoneDesrip ?? p.ZoneDesripEn)
      : (p.ZoneDesripEn ?? p.ZoneDesrip ?? p.ZoneDesripAr);
    const html = `
      <div class="text-sm text-slate-800 space-y-1">
        <div><strong>${t('map.zone.code')}:</strong> ${p.code ?? t('map.notProvided')}</div>
        <div><strong>${t('map.zone.name')}:</strong> ${zoneName ?? t('map.notProvided')}</div>
        <div><strong>${t('map.zone.description')}:</strong> ${zoneDesc ?? t('map.notProvided')}</div>
      </div>`;
    (layer as L.Layer).bindPopup?.(html, {
      className: 'zone-popup',
      maxWidth: 320,
    });
  };

  const onEachPlot: GeoJSONProps['onEachFeature'] = (feature, layer) => {
    const p = (feature as PlotFeature).properties;
    const id = p.pkPlotID ?? p.id ?? (p as any).PlotNo ?? (p as any).PlotNum ?? t('map.notProvided');
    const name = (() => {
      const arCandidates = [
        p.ArabicName,
        p.EnglishName,
        (p as any).EstatusAr,
        (p as any).EstatusEn,
        (p as any).PlotNo,
        (p as any).PlotNum,
      ];
      const enCandidates = [
        p.EnglishName,
        p.ArabicName,
        (p as any).EstatusEn,
        (p as any).EstatusAr,
        (p as any).PlotNo,
        (p as any).PlotNum,
      ];
      const list = isAr ? arCandidates : enCandidates;
      const found = list.find((v) => v != null && String(v).trim() !== '');
      return found ?? t('map.notProvided');
    })();
    const areaVal = p.SHAPE_Area ?? (p as any).Shape_Area ?? p.Shape__Area;
    const area = areaVal != null ? formatDecimal(areaVal) : t('map.notProvided');

    const html = `
      <div class="text-sm text-slate-800 space-y-1">
        <div><strong>${t('map.plot.id')}:</strong> ${id}</div>
        <div><strong>${t('map.plot.name')}:</strong> ${name}</div>
        <div><strong>${t('map.plot.area')}:</strong> ${area}</div>
      </div>`;
    (layer as L.Layer).bindPopup?.(html, {
      className: 'plot-popup',
      maxWidth: 360,
    });
    (layer as L.Path).bringToFront?.();
  };

  return (
    <div className="h-full w-full relative">
      <a
        href="https://omap.om/"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-2 right-2 sm:top-3 sm:right-3 z-[1000] inline-flex items-center gap-1 rounded-full bg-white/90 hover:bg-white text-[#005287] border shadow px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium"
        aria-label="Open OMAP external map"
        title="Open OMAP (external)"
      >
        <span className="hidden sm:inline">View on OMAP</span>
        <span className="sm:hidden">OMAP</span>
        <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" aria-hidden="true" />
      </a>
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        maxBounds={OMAN_BOUNDS}
        minZoom={6}
        maxZoom={18}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://carto.com/">CartoDB</a>'
          noWrap
        />

        {zonesFC.features.length > 0 && (
          <GeoJSON
            key={`zones-${zonesFC.features.map((f) => f.id ?? f.properties?.code).join('-')}`}
            data={zonesFC}
            style={() => zoneStyle}
            onEachFeature={onEachZone}
          />
        )}

        {plotsFC.features.length > 0 && (
          <GeoJSON
            key={`plots-${plotsFC.features.map((f) => f.properties?.pkPlotID ?? f.properties?.id).join('-')}`}
            data={plotsFC}
            style={() => plotStyle}
            onEachFeature={onEachPlot}
          />
        )}

        <AutoZoom zones={zonesFC.features} plots={plotsFC.features} fallbackBounds={OMAN_BOUNDS} />
      </MapContainer>
    </div>
  );
};

export default memo(MapView);
